import { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PhotoUploadProps {
  onPhotoUploaded: (url: string) => void;
  currentPhotoUrl?: string;
  onPhotoRemoved: () => void;
}

export const PhotoUpload = ({ onPhotoUploaded, currentPhotoUrl, onPhotoRemoved }: PhotoUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({
        variant: "destructive",
        title: "Format non supporté",
        description: "Veuillez sélectionner une image (JPEG, PNG, etc.)"
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast({
        variant: "destructive", 
        title: "Fichier trop volumineux",
        description: "La taille maximale autorisée est de 5MB"
      });
      return;
    }

    setUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          variant: "destructive",
          title: "Erreur d'authentification",
          description: "Vous devez être connecté pour uploader une photo"
        });
        return;
      }

      // Remove existing photo if any
      if (currentPhotoUrl) {
        const oldPath = currentPhotoUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage
            .from('puzzle-photos')
            .remove([`${user.id}/${oldPath}`]);
        }
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('puzzle-photos')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('puzzle-photos')
        .getPublicUrl(filePath);

      onPhotoUploaded(publicUrl);
      
      toast({
        title: "Photo uploadée",
        description: "Votre photo a été uploadée avec succès"
      });

    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({
        variant: "destructive",
        title: "Erreur d'upload",
        description: "Impossible d'uploader la photo. Veuillez réessayer."
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const removePhoto = async () => {
    if (!currentPhotoUrl) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const oldPath = currentPhotoUrl.split('/').pop();
      if (oldPath) {
        await supabase.storage
          .from('puzzle-photos')
          .remove([`${user.id}/${oldPath}`]);
      }

      onPhotoRemoved();
      
      toast({
        title: "Photo supprimée",
        description: "Votre photo a été supprimée"
      });

    } catch (error) {
      console.error('Error removing photo:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer la photo"
      });
    }
  };

  if (currentPhotoUrl) {
    return (
      <Card className="p-4 bg-background border-primary">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-primary flex items-center gap-2">
            <ImageIcon className="h-4 w-4" />
            Photo sélectionnée
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={removePhoto}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="aspect-video rounded-lg overflow-hidden bg-muted">
          <img
            src={currentPhotoUrl}
            alt="Photo pour le puzzle"
            className="w-full h-full object-cover"
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Cette photo sera utilisée pour créer votre puzzle personnalisé
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4 bg-background border-primary">
      <h3 className="text-sm font-medium text-primary mb-3 flex items-center gap-2">
        <Upload className="h-4 w-4" />
        Upload de votre photo
      </h3>
      
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive 
            ? "border-primary bg-primary/5" 
            : "border-border hover:border-primary"
        }`}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
      >
        <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm text-foreground mb-2">
          Glissez votre photo ici ou
        </p>
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="mb-2"
        >
          {uploading ? "Upload en cours..." : "Choisir un fichier"}
        </Button>
        <p className="text-xs text-muted-foreground">
          Formats acceptés: JPEG, PNG (max 5MB)
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />
    </Card>
  );
};